import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
    Users, 
    UserPlus, 
    Trash2, 
    Loader2, 
    Mail,
    CheckCircle2,
    Clock,
    X,
    Crown
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

export default function TeamManagementPage() {
    const { token, user } = useAuth();
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [subscription, setSubscription] = useState(null);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviting, setInviting] = useState(false);
    const [inviteForm, setInviteForm] = useState({
        email: '',
        first_name: '',
        last_name: ''
    });

    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    useEffect(() => {
        fetchTeamMembers();
        fetchSubscription();
    }, []);

    const fetchTeamMembers = async () => {
        try {
            const response = await fetch(`${API}/team/members`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setMembers(data.members);
            }
        } catch (error) {
            console.error('Error fetching team members:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchSubscription = async () => {
        try {
            const response = await fetch(`${API}/subscription/my-subscription`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setSubscription(data.subscription);
            }
        } catch (error) {
            console.error('Error fetching subscription:', error);
        }
    };

    const handleInvite = async (e) => {
        e.preventDefault();
        setInviting(true);

        try {
            const response = await fetch(`${API}/team/invite`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(inviteForm)
            });

            if (response.ok) {
                toast.success('Team member invited successfully');
                setShowInviteModal(false);
                setInviteForm({ email: '', first_name: '', last_name: '' });
                fetchTeamMembers();
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to invite team member');
            }
        } catch (error) {
            toast.error('Error inviting team member');
        } finally {
            setInviting(false);
        }
    };

    const handleRemoveMember = async (memberId) => {
        if (!confirm('Are you sure you want to remove this team member?')) return;

        try {
            const response = await fetch(`${API}/team/members/${memberId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                toast.success('Team member removed');
                fetchTeamMembers();
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to remove team member');
            }
        } catch (error) {
            toast.error('Error removing team member');
        }
    };

    const teamSeatsLimit = subscription?.features?.team_seats || 0;
    const usedSeats = members.filter(m => m.status !== 'removed').length;
    const canInvite = usedSeats < teamSeatsLimit;

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[60vh]">
                    <Loader2 className="w-8 h-8 animate-spin text-[#F51E1E]" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                            <Users className="w-7 h-7 text-[#F51E1E]" />
                            Team Management
                        </h1>
                        <p className="text-[#A3A3A3] mt-1">
                            Invite team members to help manage your projects
                        </p>
                    </div>

                    <div className="flex items-center gap-4">
                        <Badge className={`${usedSeats >= teamSeatsLimit ? 'bg-orange-500/20 text-orange-400' : 'bg-[#333] text-[#A3A3A3]'} px-3 py-1`}>
                            {usedSeats} / {teamSeatsLimit} seats used
                        </Badge>
                        
                        {canInvite ? (
                            <Button
                                onClick={() => setShowInviteModal(true)}
                                className="bg-[#F51E1E] hover:bg-[#D41212] text-white"
                                data-testid="invite-member-btn"
                            >
                                <UserPlus className="w-4 h-4 mr-2" />
                                Invite Member
                            </Button>
                        ) : (
                            <Button
                                onClick={() => window.location.href = '/pricing'}
                                className="bg-purple-500 hover:bg-purple-600 text-white"
                            >
                                <Crown className="w-4 h-4 mr-2" />
                                Upgrade for More Seats
                            </Button>
                        )}
                    </div>
                </div>

                {/* Team Members List */}
                {teamSeatsLimit === 0 ? (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-[#1A1A1A] border border-[#333] rounded-xl p-8 text-center"
                    >
                        <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-4">
                            <Users className="w-8 h-8 text-purple-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-white mb-2">Team Seats Not Available</h3>
                        <p className="text-[#A3A3A3] mb-4">
                            Upgrade to Pro or Studio plan to invite team members.
                        </p>
                        <Button
                            onClick={() => window.location.href = '/pricing'}
                            className="bg-purple-500 hover:bg-purple-600 text-white"
                        >
                            View Plans
                        </Button>
                    </motion.div>
                ) : members.length === 0 ? (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-[#1A1A1A] border border-[#333] rounded-xl p-8 text-center"
                    >
                        <div className="w-16 h-16 rounded-full bg-[#262626] flex items-center justify-center mx-auto mb-4">
                            <Users className="w-8 h-8 text-[#666]" />
                        </div>
                        <h3 className="text-lg font-semibold text-white mb-2">No Team Members Yet</h3>
                        <p className="text-[#A3A3A3] mb-4">
                            Invite team members to collaborate on your projects.
                        </p>
                        <Button
                            onClick={() => setShowInviteModal(true)}
                            className="bg-[#F51E1E] hover:bg-[#D41212] text-white"
                        >
                            <UserPlus className="w-4 h-4 mr-2" />
                            Invite Your First Member
                        </Button>
                    </motion.div>
                ) : (
                    <div className="space-y-3">
                        {members.map((member, index) => (
                            <motion.div
                                key={member.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className="bg-[#1A1A1A] border border-[#333] rounded-xl p-4 flex items-center justify-between"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-full bg-[#262626] flex items-center justify-center">
                                        <span className="text-sm font-bold text-white">
                                            {member.first_name?.[0]}{member.last_name?.[0]}
                                        </span>
                                    </div>
                                    <div>
                                        <p className="font-medium text-white">
                                            {member.first_name} {member.last_name}
                                        </p>
                                        <p className="text-sm text-[#888] flex items-center gap-2">
                                            <Mail className="w-3 h-3" />
                                            {member.email}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3">
                                    <Badge className={`
                                        ${member.status === 'active' ? 'bg-green-500/20 text-green-400 border-green-500/30' : ''}
                                        ${member.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' : ''}
                                    `}>
                                        {member.status === 'active' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                                        {member.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                                        {member.status.charAt(0).toUpperCase() + member.status.slice(1)}
                                    </Badge>

                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRemoveMember(member.id)}
                                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}

                {/* Invite Modal */}
                <AnimatePresence>
                    {showInviteModal && (
                        <>
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                onClick={() => setShowInviteModal(false)}
                                className="fixed inset-0 bg-black/70 z-50"
                            />
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                className="fixed inset-0 z-50 flex items-center justify-center p-4"
                            >
                                <div className="bg-[#1A1A1A] border border-[#333] rounded-xl w-full max-w-md p-6">
                                    <div className="flex items-center justify-between mb-6">
                                        <h3 className="text-lg font-semibold text-white">Invite Team Member</h3>
                                        <button
                                            onClick={() => setShowInviteModal(false)}
                                            className="text-[#888] hover:text-white"
                                        >
                                            <X className="w-5 h-5" />
                                        </button>
                                    </div>

                                    <form onSubmit={handleInvite} className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <Label className="text-[#A3A3A3]">First Name</Label>
                                                <Input
                                                    value={inviteForm.first_name}
                                                    onChange={(e) => setInviteForm({ ...inviteForm, first_name: e.target.value })}
                                                    required
                                                    className="bg-[#262626] border-[#444] text-white"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-[#A3A3A3]">Last Name</Label>
                                                <Input
                                                    value={inviteForm.last_name}
                                                    onChange={(e) => setInviteForm({ ...inviteForm, last_name: e.target.value })}
                                                    required
                                                    className="bg-[#262626] border-[#444] text-white"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <Label className="text-[#A3A3A3]">Email</Label>
                                            <Input
                                                type="email"
                                                value={inviteForm.email}
                                                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                                                required
                                                className="bg-[#262626] border-[#444] text-white"
                                            />
                                        </div>

                                        <p className="text-xs text-[#666]">
                                            An account will be created for this team member with access to your projects.
                                        </p>

                                        <div className="flex gap-3 pt-2">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                onClick={() => setShowInviteModal(false)}
                                                className="flex-1 border-[#444] text-white"
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                type="submit"
                                                disabled={inviting}
                                                className="flex-1 bg-[#F51E1E] hover:bg-[#D41212] text-white"
                                            >
                                                {inviting ? (
                                                    <>
                                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                        Inviting...
                                                    </>
                                                ) : (
                                                    'Send Invite'
                                                )}
                                            </Button>
                                        </div>
                                    </form>
                                </div>
                            </motion.div>
                        </>
                    )}
                </AnimatePresence>
            </div>
        </Layout>
    );
}
